import React from 'react';
import styled from 'styled-components';
import {SelectionBox} from './Odds';
import {PeriodGroupedMarkets} from "../../../../markets/RequiredDisplayMarketTitles";
import {
    MarketGroup,
    marketGroupFunctions,
    MarketMenuGroup,
    marketToRows,
    TableCell
} from "../../../../markets/marketMapping";
import {useDispatch, useSelector} from "react-redux";
import {StoreType} from "../../../../store/spirs/types";
import {FixtureElement, MarketElement} from "../../../../models/api/codegen/code";
import {CouponEvent} from "../../../../store/spirs/reducers/couponReducer";
import {
    ADD_COUPON_EVENT,
    CHANGE_NEED_CONFIRM_DIALOG_SETTINGS,
    getMarketLimitAsync,
    NEED_CONFIRM_COUPON_EVENT,
    REMOVE_COUPON_EVENT,
    UPDATE_COUPON_MESSAGE
} from "../../../../store/spirs/actions";
import {useRate} from "../../../FixtureVerbose/DefaultRate";
import {useTranslation} from "react-i18next";
import {showCustomToast} from '../../../../utils/Toasts';
import { Desktop } from '../../../adaptive/Desktop';
import { Mobile } from '../../../adaptive/Mobile';

const PeriodMarketsContainer = styled.div`
    display: grid;
    margin-right: 29px;
    margin-left: 45px;
    gap: 2px;
    grid-template-columns: 353px 1fr 1fr;
    border-top: 1px solid #272d33;
    background-color: #1d2126;
    font-size: 12px;
    &:last-child {
        border-radius: 0 0 8px 8px;
    }
`;
PeriodMarketsContainer.displayName = "PeriodMarketsContainer";

const PeriodMarketBox = styled.div`
    display: flex;
    justify-content: space-between;
    flex-basis: 347px;
    padding-top: 7px;
    padding-bottom: 6px;
    padding-left: 13px;
    padding-right: 7px;
    color: #ababab;
`;

const Score = styled.span`
    padding-right: 12px;
`;

type PeriodMarketProps = {
    period: PeriodGroupedMarkets;
    fixtureId: number;
    marketGroupsToDisplay: MarketGroup[];
    mobileMarketGroup: MarketGroup;
    competitionName: string;
    status: FixtureElement['s'];
    score: string;
    isLast: boolean;
    suspended: boolean;
    hasOverlay: boolean;
};

export const PeriodMarket = ({period, fixtureId, marketGroupsToDisplay, mobileMarketGroup, competitionName, status, isLast, suspended, hasOverlay}: PeriodMarketProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const currentCouponType = useSelector((state: StoreType) => state.spirs.coupons.currentCouponType);
    const currentCouponEvents = useSelector((state: StoreType) => state.spirs.coupons[currentCouponType]);
    const needConfirm = useSelector((state: StoreType) => state.spirs.playerSettings.needConfirm);
    const playerToken = useSelector((state: StoreType) => state.spirs.playerToken);
    const rate = useRate(status);
    const handleClickOddCell = React.useCallback(
        (tCell: TableCell, market: MarketElement) => (event: React.MouseEvent) => {
            if (hasOverlay) {
                return;
            }
            const alreadyAdded = currentCouponEvents.find(event => event.selectionId === tCell.selectionId);
            if (alreadyAdded) {
                dispatch(REMOVE_COUPON_EVENT(tCell.selectionId));
                return;
            }
            if (playerToken === 'GUEST' && currentCouponEvents.length >= 10) {
                showCustomToast(t('coupons.guestLimitReached'));
                return;
            }
            if (market.s != 'ACTIVE' || tCell.isLabel || tCell.locked || suspended) return;

            if ((currentCouponType === 'EXPRESS' || currentCouponType === 'SYSTEM')
                && currentCouponEvents.find((el: CouponEvent) => el.fixtureId === fixtureId)) {
                dispatch(UPDATE_COUPON_MESSAGE(t('coupons.alreadyExistFixtureMessage')));
            } else {
                dispatch(UPDATE_COUPON_MESSAGE(''));
                const couponEvent: CouponEvent = {
                    fixtureId,
                    selectionName: tCell.selectionName,
                    competitionName: competitionName,
                    selectionId: tCell.selectionId,
                    rate: rate,
                    active: true,
                    odds: Number(tCell.value),
                    marketName: market.n,
                    marketId: tCell.marketId,
                    minRate: market.mi,
                    maxRate: market.ma,
                    live: status === 'LIVE',
                };
                if (needConfirm) {
                    dispatch(NEED_CONFIRM_COUPON_EVENT(couponEvent));
                    dispatch(CHANGE_NEED_CONFIRM_DIALOG_SETTINGS(event.currentTarget.id));
                } else {
                    dispatch(ADD_COUPON_EVENT(couponEvent));
                }
                dispatch(getMarketLimitAsync.request({
                    marketId: couponEvent.marketId,
                    fixtureId: couponEvent.fixtureId
                }));
            }
        }, [currentCouponType, currentCouponEvents, rate, needConfirm, hasOverlay]);
    const isSelect = (selectionId: number) => currentCouponEvents.map(s => s.selectionId).indexOf(selectionId) > -1;


    const mobilePeriodGroups = MarketMenuGroup.find(main => main.name === mobileMarketGroup);
    const mobileMarkets = period.groups.filter(p => mobilePeriodGroups && mobilePeriodGroups.groups.indexOf(p.group) > -1);
    const mobileMarket = mobileMarkets.length > 0
        ? mobileMarkets[0].groupedMarkets ? mobileMarkets[0].groupedMarkets.m[0] : null
        : null;
    const mobileGroup = marketToRows[mobileMarketGroup];

    return (<>
        <Desktop>
            <div className="flex gap-[10px]">
                {marketGroupsToDisplay.length === 1 && <div style={{ backgroundColor: '#171A1E', height: '100%' }} />}
                {marketGroupsToDisplay.map((marketGroup, index) => {
                    const periodGroups = MarketMenuGroup.find(main => main.name === marketGroup);
                    const markets = period.groups.filter(p => periodGroups && periodGroups.groups.indexOf(p.group) > -1);
                    const market = markets.length > 0
                        ? markets[0].groupedMarkets ? markets[0].groupedMarkets.m[0] : null
                        : null;
                    const groups = marketToRows[marketGroup];
                    const selections = market ? marketGroupFunctions[marketGroup](market.sel, market.i) : [];
                    return (
                        <div
                            key={index}
                            style={{ gridTemplateColumns: `repeat(${groups.length},1fr)` }}
                            className={`w-[130px] desktop:w-[250px] grid gap-[5px] text-[14px] h-full`}
                        >
                            {!market
                                ? groups.map((tableCellTitle, gi) => (
                                    <div className={hasOverlay ? "opacity-25 pointer-events-none" : ""}>
                                        <SelectionBox key={gi} disabled={true} groups={groups} />
                                    </div>
                                ))

                                : selections.map((selection, mi) =>
                                    <div key={mi}
                                         id={'selection-id-' + selection.selectionId}
                                         onClick={handleClickOddCell(selection, market)}
                                         className={hasOverlay ? "opacity-25 pointer-events-none" : ""}>
                                        <SelectionBox
                                            isLast={isLast && mi === selections.length - 1 && index === marketGroupsToDisplay.length - 1}
                                            disabled={market.s !== 'ACTIVE' || suspended}
                                            isSuspended={market.s === 'SUSPENDED' || suspended}
                                            isLocked={selection.locked}
                                            isLabel={selection.isLabel}
                                            isSelected={isSelect(selection.selectionId)}
                                            value={selection.value}
                                            selectionName={selection.value + ""}
                                            groups={groups}
                                        />
                                    </div>
                                )
                            }
                        </div>
                    );
                })}
            </div>
        </Desktop>
        <Mobile>
            <div
                style={{ gridTemplateColumns: `repeat(${mobileGroup.length}, 1fr)` }}
                className={`tablet:w-[130px] desktop:w-[250px] grid gap-[2px] tablet:gap-[5px] text-[14px] `}
            >
                {!mobileMarket
                    ? mobileGroup.map((tableCellTitle, gi) => (
                        <div className={hasOverlay ? "opacity-25 pointer-events-none" : ""}>
                            <SelectionBox key={gi} disabled={true} groups={mobileGroup} />
                        </div>
                    ))

                    : (marketGroupFunctions[mobileMarketGroup](mobileMarket.sel, mobileMarket.i)).map((selection, mi) =>
                        <div key={mi}
                             id={'selection-id-' + selection.selectionId}
                             onClick={handleClickOddCell(selection, mobileMarket)}
                             className={hasOverlay ? "opacity-25 pointer-events-none" : ""}>
                            <SelectionBox
                                isLast={isLast && mi === mobileMarket.sel.length - 1 && 0 === marketGroupsToDisplay.length - 1}
                                disabled={mobileMarket.s !== 'ACTIVE' || suspended}
                                isSuspended={mobileMarket.s === 'SUSPENDED' || suspended}
                                isLocked={selection.locked}
                                isLabel={selection.isLabel}
                                isSelected={isSelect(selection.selectionId)}
                                value={selection.value}
                                selectionName={selection.value + ""}
                                groups={mobileGroup}
                            />
                        </div>
                    )
                }
            </div>
        </Mobile>
    </>);
};
